<template>
    <div class="nominee-shareholder-create-or-edit">
        <b-sidebar
            id="sidebar-task-handler"
            data-test="sidebar"
            sidebar-class="sidebar-lg"
            :visible="isNomineeShareholderSidebarActive"
            bg-variant="white"
            shadow
            backdrop
            no-header
            right
            @change="
                (val) => $emit('update:is-nominee-shareholder-sidebar-active', val)
            "
            @hidden="clearFormData"
        >
            <template #default="{ hide }">
                <!-- Header -->
                <div
                    class="
                        d-flex
                        justify-content-between
                        align-items-center
                        content-sidebar-header
                        px-2
                        py-1
                        side-bar-header
                    "
                >
                    <h3 class="mb-0">
                        {{ sideBarTitle }}
                    </h3>

                    <div>
                        <feather-icon
                            class="ml-1 cursor-pointer"
                            icon="XIcon"
                            size="16"
                            @click="hide"
                        />
                    </div>
                </div>

                <!-- Body -->
                <validation-observer
                    v-slot="{ handleSubmit }"
                    ref="refFormObserver"
                >
                    <!-- Form -->
                    <b-form
                        class="p-2"
                        @submit.prevent="handleSubmit(onSubmit)"
                        @reset.prevent="resetForm"
                    >
                        <!-- Title -->
                        <validation-provider
                            v-slot="{ errors }"
                            name="nominee shareholder name"
                            rules="required"
                        >
                            <b-form-group label="Nominee shareholder name" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nominee-shareholder-name"
                                    ref="nomineeShareholder"
                                    v-model="nomineeShareholderLocal.nomineeShareholder"
                                    class="custom-select-icon"
                                    placeholder="Select nominee shareholder"
                                    label="name" 
                                    :clearable="false"
                                    :disabled = "isDisabled || dataStatus === 'edit'"
                                    :options="nomineeShareholderOptions"
                                    @input="handleOnchangeNomineeShareholder"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nomineeShareholderOptions.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="appointment date"
                            rules="required"
                            >
                            <b-form-group label="Date on which the shareholder became the nominee" :state="errors.length > 0 ? false : null">
                                <flat-pickr
                                    v-model="nomineeShareholderLocal.appointmentDate"
                                    placeholder="Pick a date"
                                    class="form-control"
                                    :config="datePickerConfig"
                                    :disabled = "isDisabled"
                                    :style="dynamicStyle"
                                    data-test="appointment-date"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <validation-provider
                            v-slot="{ errors }"
                            name="cessation date"
                            >
                                <b-form-group label="Date on which the shareholder ceased to be the nominee" :state="errors.length > 0 ? false : null">
                                    <flat-pickr
                                        v-model="nomineeShareholderLocal.cessationDate"
                                        placeholder="Pick a date"
                                        class="form-control"
                                        :config="datePickerConfig"
                                        :disabled = "isDisabled"
                                        :style="dynamicStyle"
                                        data-test="cessation-date"
                                    />
                                <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                        </validation-provider>

                        <div
                            v-if="showAddNewNominatorButton"
                            class="d-flex align-items-center justify-content-between"
                        >
                             <b-button
                                variant="outline-primary"
                                class="btn btn-sm d-flex justify-content-end align-items-center mb-1"
                                style="font-size: 1em;"
                                data-test="add-existing-nominator-btn"
                                :disabled="isExistingNominator"
                                @click="showNominatorField('existing', dataStatus)"
                            >
                                Existing Nominator
                                <vector
                                    data-name="add-user"
                                    data-width="15px"
                                    data-height="12px"
                                    class="ml-.3 d-flex justify-content-center align-items-center"
                                />
                            </b-button>

                            <b-button
                                variant="outline-primary"
                                class="btn btn-sm d-flex justify-content-end align-items-center mb-1"
                                style="font-size: 1em;"
                                data-test="add-new-nominator-btn"
                                :disabled="!isExistingNominator"
                                @click="showNominatorField('new', dataStatus)"
                            >
                                New Nominator
                                <vector
                                    data-name="add-user"
                                    data-width="15px"
                                    data-height="12px"
                                    class=" ml-.3 d-flex justify-content-center align-items-center"
                                />
                            </b-button>
                        </div>

                         <validation-provider
                            v-slot="{ errors }"
                            name="nominator type"
                            rules="required"
                        >
                            <b-form-group label="Nominator type" :state="errors.length > 0 ? false : null">
                                <v-select
                                    id="nominatorType"
                                    ref="nominatorType"
                                    v-model="nomineeShareholderLocal.nominatorType"
                                    class="custom-select-icon text-capitalize"
                                    placeholder="Select nominator type"
                                    label="text" 
                                    :clearable="false"
                                    :disabled = "isDisabled"
                                    :options="nominatorTypeOptions"
                                    @input="onNominatorTypeChange"
                                >
                                    <div slot="no-options" class="d-flex justify-content-center">
                                        <normal-loader v-if="nominatorTypeOptions.length <= 0" />
                                        <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                    </div>
                                </v-select>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <div v-if="!isNewNominator">
                             <validation-provider
                                v-slot="{ errors }"
                                :name="dynamicNameLabel.toLowerCase()"
                                rules="required"
                                mode="lazy"
                            >
                                <b-form-group
                                    :label="dynamicNameLabel"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <v-select
                                        id="name"
                                        ref="nominatorName"
                                        v-model="nomineeShareholderLocal.nominator"
                                        :placeholder="dynamicNamePlaceholder"
                                        :state="errors.length > 0 ? false : null"
                                        data-test="nominator-name"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="name"
                                        :disabled = "isDisabled"
                                        :options="nominators"
                                        @input="handleOnNominatorChange"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader />
                                            <span v-if="! nomineeShareholderLocal.nominatorType">
                                                P<span class="text-lowercase">lease select the nominator type first.</span>
                                            </span>
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>

                        <div v-if="isNewNominator">
                            <validation-provider
                                v-slot="validationContext"
                                :name="dynamicIdLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group>
                                    <slot name="label">
                                        <label id="nominator-id" for="id">
                                            {{ dynamicIdLabel }}
                                        </label>
                                    </slot>

                                    <b-form-input
                                        id="id"
                                        v-model="newNominator.nric"
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        :placeholder="dynamicIdPlaceholder"
                                        trim
                                        data-test ="nric"
                                        @input="requestAutoFillFormData"
                                    />

                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                            <div v-if="isNominatorTypeIndividual">
                                <validation-provider
                                    v-slot="{ errors }"
                                    name="id type"
                                    rules="required"
                                >
                                    <b-form-group
                                        label="ID type"
                                        :state="errors.length > 0 ? false : null"
                                    >
                                        <v-select
                                            ref="idType"
                                            v-model="newNominator.idType"
                                            class="custom-select-icon"
                                            :clearable="false"
                                            :disabled = "isDisabled"
                                            placeholder="Select ID type"
                                            label="name"
                                            :options="idType_options"
                                            @input="handleNationalityOnIdTypeChanged"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>

                                <validation-provider
                                    v-slot="{ errors }"
                                    name="nationality"
                                    rules="required"
                                >
                                    <b-form-group 
                                        label="Nationality"
                                        :state="errors.length > 0 ? false : null"
                                    >
                                        <v-select
                                            id="nationality"
                                            ref="nationality"
                                            v-model="newNominator.nationality"
                                            class="custom-select-icon"
                                            label="text" 
                                            placeholder="Select nationality"
                                            :clearable="false"
                                            :disabled ="isDisabled || shouldDisabledNationality"
                                            :options="nationalityOptions"
                                        >
                                            <div slot="no-options" class="d-flex justify-content-center">
                                                <normal-loader v-if="nationalityOptions.length <= 0" />
                                                <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                            </div>
                                        </v-select>
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>

                                <validation-provider
                                    v-if="shouldShowExpiryDate"
                                    v-slot="{ errors }"
                                    name="date of expiry"
                                    rules="required"
                                >
                                    <b-form-group :state="errors.length > 0 ? false : null">
                                        <slot name="label">
                                            <label id="dateOfExpiry" for="id">
                                                Date of expiry
                                                <vector
                                                    data-type="icon"
                                                    data-width="15px"
                                                    data-height="15px"
                                                    data-class="mr-.5"
                                                    data-name="information"
                                                ></vector>
                                            </label>
                                        </slot>

                                        <b-popover
                                            target="dateOfExpiry"
                                            triggers="hover"
                                            placement="right"
                                            variant="black"
                                        >
                                            Skip this if you are a Singapore citizen or permanent
                                            resident.
                                            <br />
                                            Fill in the date of expiry of your passport or FIN if
                                            you are a foreigner.
                                        </b-popover>

                                        <flat-pickr
                                            v-model="newNominator.dateOfExpiry"
                                            placeholder="Enter date of expiry"
                                            class="form-control"
                                            :config="datePickerConfig"
                                            :disabled = "isDisabled"
                                            data-test="date-picker-dateOfExpiry"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>
                            </div>

                            <validation-provider 
                                v-slot="validationContext"
                                :name="dynamicNewNominatorNameLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group :label="dynamicNewNominatorNameLabel">
                                    <b-form-input
                                        id="name"
                                        v-model="newNominator.name"
                                        :placeholder="dynamicNewNominatorPlaceholder"
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        data-test="nominator-name"
                                        :disabled = "isDisabled"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>

                             <div v-if="isNominatorTypeIndividual">
                                <validation-provider
                                    v-slot="validationContext"
                                    name="individual nominator aliases"
                                >
                                    <b-form-group label="Individual nominator aliases, if any">
                                        <b-form-input
                                            id="alias"
                                            ref="alias"
                                            v-model="newNominator.alias"
                                            placeholder="individual nominator aliases"
                                            :state="
                                                getValidationState(validationContext)
                                            "
                                            :disabled = "isDisabled"
                                            trim
                                            data-test="alias"
                                        />
                                        <b-form-invalid-feedback>
                                            {{ validationContext.errors[0] }}
                                        </b-form-invalid-feedback>
                                    </b-form-group>
                                </validation-provider>

                                <validation-provider
                                    v-slot="{ errors }"
                                    name="date of birth"
                                    rules="required"
                                >
                                    <b-form-group 
                                        label="Date of birth"
                                        :state="errors.length > 0 ? false : null"
                                    >
                                        <flat-pickr
                                            v-model="newNominator.dob"
                                            class="form-control"
                                            data-test="date-of-birth"
                                            placeholder="Enter date of birth"
                                            :config="datePickerConfig"
                                            :disabled = "isDisabled"
                                            :style="dynamicStyle"
                                            :state="errors.length > 0 ? false : null"
                                        />
                                        <small class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>
                            </div>

                            <validation-provider
                                v-slot="validationContext"
                                :name="dynamicAddressLabel.toLowerCase()"
                                rules="required"
                            >
                                <b-form-group :label="dynamicAddressLabel">
                                    <b-form-textarea
                                        id="textarea"
                                        v-model="newNominator.address"
                                        :placeholder="dynamicAddressPlaceholder"
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        trim
                                        rows="3"
                                        max-rows="3"
                                        data-test="address"
                                    >
                                    </b-form-textarea>
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                       </div>

                        <!-- < INDIVIDUAL NOMINATOR > -->
                        <div v-if="isNominatorTypeIndividual && !isNewNominator">
                            <validation-provider
                                v-slot="validationContext"
                                name="individual nominator aliases"
                            >
                                <b-form-group label="Individual nominator aliases, if any">
                                    <b-form-input
                                        id="alias"
                                        ref="nominatorAlias"
                                        v-model="nomineeShareholderLocal.alias"
                                        placeholder="Individual nominator aliases"
                                        :state="
                                            getValidationState(validationContext)
                                        "
                                        :disabled = "isDisabled"
                                        trim
                                        data-test="alias"
                                    />
                                    <b-form-invalid-feedback>
                                        {{ validationContext.errors[0] }}
                                    </b-form-invalid-feedback>
                                </b-form-group>
                            </validation-provider>
                        </div>
                        <!-- </ INDIVIDUAL NOMINATOR > -->

                        <!-- < CORPORATE NOMINATOR > -->
                        <div v-if="! isNominatorTypeIndividual">
                            <validation-provider 
                                v-slot="{ errors }"
                                name="corporate nominator UEN"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator UEN issued by the registrar (ACRA)">
                                    <b-form-input
                                        id="name"
                                        v-model="nomineeShareholderLocal.uen"
                                        placeholder="Enter corporate nominator UEN"
                                        :state="errors.length > 0 ? false : null"
                                        data-test="uen"
                                        :disabled = "isDisabled"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider
                                v-slot="{ errors }"
                                name="corporate nominator legal form"
                                rules="required"
                            >
                                <b-form-group
                                    label="Corporate nominator legal form"
                                    :state="errors.length > 0 ? false : null"
                                >
                                    <v-select
                                        ref="legalForm"
                                        v-model="nomineeShareholderLocal.legalForm"
                                        placeholder="Select corporate nominator legal form"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        :disabled = "isDisabled"
                                        :options="legalForms"
                                        label="text"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="legalForms.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                v-slot="{ errors }"
                                name="corporate nominator jurisdiction"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator jurisdiction" :state="errors.length > 0 ? false : null">
                                        <v-select
                                        ref ="jurisdiction"
                                        v-model="nomineeShareholderLocal.jurisdiction"
                                        :clearable="false"
                                        :options="jurisdictions"
                                        label="name"
                                        value="id"
                                        :disabled = "isDisabled"
                                        class="custom-select-icon text-capitalize"
                                        placeholder="Select corporate nominator jurisdiction"
                                        @input="onNominatorJurisdictionChange"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader v-if="jurisdictions.length <= 0" />
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                v-slot="{ errors }"
                                name="corporate nominator statue"
                                rules="required"
                            >
                                <b-form-group label="Corporate nominator statue under which the corporate is formed" :state="errors.length > 0 ? false : null">
                                    <v-select
                                        ref="statue"
                                        v-model="nomineeShareholderLocal.statue"
                                        placeholder="Select corporate nominator statue"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        label="name"
                                        :disabled = "isDisabled"

                                        value="id"
                                        :options="statues"
                                        :state="errors.length > 0 ? false : null"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader />
                                            <span v-if="! nomineeShareholderLocal.jurisdiction">
                                                P<span class="text-lowercase">lease select the jurisdiction first.</span>
                                            </span>
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>

                            <validation-provider 
                                v-slot="{ errors }"
                                name="registrar"
                                rules="required"
                            >
                                <b-form-group :state="errors.length > 0 ? false : null" label="Name of corporate nominator's register of the jurisdiction where the legal entity is formed or incorporated">
                                    <v-select
                                        ref="registrar"
                                        v-model="nomineeShareholderLocal.registrar"
                                        class="custom-select-icon text-capitalize"
                                        :clearable="false"
                                        :disabled = "isDisabled"
                                        label="name"
                                        value="id"
                                        :options="registrars"
                                        :state="errors.length > 0 ? false : null"
                                        placeholder="Select corporate registrar"
                                    >
                                        <div slot="no-options" class="d-flex justify-content-center">
                                            <normal-loader />
                                             <span v-if="! nomineeShareholderLocal.jurisdiction">
                                                P<span class="text-lowercase">lease select the jurisdiction first.</span>
                                            </span>
                                            <span v-else> {{ NO_MATCHING_OPTIONS }} </span>
                                        </div>
                                    </v-select>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </b-form-group>
                            </validation-provider>
                        </div>
                        <!-- < /CORPORATE NOMINATOR > -->

                        <validation-provider
                            v-slot="{ errors }"
                            name="remark"
                        >
                            <b-form-group label="Remarks">
                                <b-form-input
                                    id="remark"
                                    v-model="nomineeShareholderLocal.remark"
                                    placeholder="Enter nominee shareholder remark"
                                    :state="errors.length > 0 ? false : null"
                                    :disabled = "isDisabled"
                                    data-test="remark"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </b-form-group>
                        </validation-provider>

                        <!-- Form Actions -->
                        <div class="d-flex">
                            <b-button
                                v-if="! isDisabled"
                                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                                :disabled="dataSidebarActionLoading"
                                variant="primary"
                                class="
                                    mr-1
                                    mt-1
                                    d-flex
                                    justify-content-center
                                    align-items-center
                                "
                                type="submit"
                                data-test="create-nominee-shareholder-btn"
                                >
                                <span :class="dataSidebarActionLoading ? 'mr-1' : ''">
                                    {{ dataStatus === 'edit' ? "Update" : "Add " }}
                                </span>

                                <img
                                    v-if="dataStatus === 'create' && !dataSidebarActionLoading"
                                    :src="'/illustrations/add-circle.svg'"
                                    class="ml-1"
                                    alt="add-logo"
                                />

                                <img
                                    v-if="dataStatus === 'edit' && !dataSidebarActionLoading"
                                    :src="'/illustrations/edit.svg'"
                                    class="ml-1"
                                    alt="edit-logo"
                                />
                                <normal-loader v-if="dataSidebarActionLoading" class="action-button left-10%" />
                            </b-button>

                            <b-button
                                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                                class="
                                mt-1 
                                d-flex
                                justify-content-center
                                align-items-center"
                                variant="outline-primary"
                                data-test="cancel"
                                @click="$emit('update:is-nominee-shareholder-sidebar-active', false)"
                                >
                                Cancel
                                <vector 
                                    data-type="icons" 
                                    data-width="15px" 
                                    data-height="15px" 
                                    data-name="cancel" 
                                    class="float-right ml-1"
                                    alt="cancel-logo"
                                />
                            </b-button>
                        </div>
                    </b-form>
                </validation-observer>
            </template>
        </b-sidebar>
    </div>
</template>

<script>
import {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BPopover,
    BButton,
    BFormTextarea,
    BFormInvalidFeedback,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { required } from "@validations";
import formValidation from "@core/comp-functions/forms/form-validation";
import { toRefs, ref, watch } from "@vue/composition-api";
import { debounce } from 'debounce';
import { NRIC_CITIZEN_ID, SINGAPORE_CITIZEN, NOMINEE_SHAREHOLDER } from "@/constants/config";
import IdType from '@/models/IdType';
import Nationality from "@/models/Nationality";
import Country from '@/models/Country';
import Nominator from '@/models/Nominator';
import Shareholder from '@/models/Shareholder';
import CompanyNomineeShareholder from '@/models/CompanyNomineeShareholder';
import { NO_MATCHING_OPTIONS } from "@/constants/errors";
import { formatDate } from '@/helpers';

export default {
    components: {
        // BSV
        BForm,
        vSelect,
        BButton,
        BSidebar,
        flatPickr,
        BFormGroup,
        BFormInput,
        BPopover,
        BFormTextarea,
        BFormInvalidFeedback,

        // Form Validation
        ValidationProvider,
        ValidationObserver,
    },

    directives: {
        Ripple,
    },

    model: {
        prop: "isNomineeShareholderSidebarActive",
        event: "update:is-nominee-shareholder-sidebar-active",
    },
    props: {
        isNomineeShareholderSidebarActive: {
            type: Boolean,
            required: true,
        },

        dataSidebarActionLoading: {
            type: Boolean,
            default: false,
        },

        dataNomineeShareholder: {
            type: Object,
            required: true,
        },
        
        dataStatus: { 
            type: String,
            default: "view"
        },

        autoFillRequestedShareholderNominator: {
            type: Object,
            default: null,
        }
    },

    data() {
        return {
            required,
            // email,

            datePickerConfig: {
                altFormat: "j M Y",
                dateFormat: "j M Y",
                disableMobile: "true"
            },

            statues: [],
            dataStatues: [],
            registrars: [],
            dataRegistrars: [],
            legalForms: [],
            nominators: [],
            jurisdictions: [],
            nominatorOptions: [],

            originNomineeShareholderData: [],
            nomineeShareholderOptions: [],
            nominatorTypeOptions: [],
            nationalityOptions: [],
            idType_options: [],
            countriesOptions: [],

            NO_MATCHING_OPTIONS,
           
            shouldDisabledNationality: false,

            forceToCheckExpiry: false,
            forceRecheckExpiryDateCount: 1,
        };
    },

    computed: {
        isDisabled() {
            if(this.dataStatus === "view")
                return true;
            return false;
        },

        showAddNewNominatorButton() {
            return this.dataStatus === 'create';
        },

        sideBarTitle() {
            if(this.dataStatus === "create")
                return "Add Nominee Shareholder";

            if(this.dataStatus === "view")
                return "Nominee Shareholder Details";

            return "Update Nominee Shareholder";
        },

        dynamicStyle() {
            if(this.dataStatus === "view")
                return "background: #efefef; cursor: context-menu!important";
            return "background: white;";
        },
        
        isNominatorTypeIndividual() {
            // if there is no type yet, act like it's Individual
            if (! this.nomineeShareholderLocal.nominatorType) return true;

            return (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL
            );
        },

        dynamicIdLabel() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Registration number";

            return "ID";
        },

        dynamicIdPlaceholder() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter registration number";

            return "Enter individual nominator id";
        },

        dynamicNameLabel() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Individual nominator name";
        },

        dynamicNamePlaceholder() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Select corporate nominator";

            return "Select individual nominator";
        },

        dynamicNewNominatorNameLabel() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Corporate nominator name";

            return "Name";
        },

        dynamicNewNominatorPlaceholder() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter corporate nominator name";

            return "Enter nominator name";
        },


        dynamicAddressLabel() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Address of registered office";

            return "Address";
        },

        dynamicAddressPlaceholder() {
            if (
                this.nomineeShareholderLocal.nominatorType &&
                this.nomineeShareholderLocal.nominatorType.text ===
                    NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE
            )
                return "Enter address of registered office";

            return "Enter address";
        },

        shouldShowExpiryDate()
        {
            if(this.forceRecheckExpiryDateCount > 0 && this.newNominator?.idType?.name.includes("NRIC")) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.newNominator.dateOfExpiry = null;
            }
            return ! this.newNominator?.idType?.name.includes("NRIC");
        },

    },

    watch: {
        dataStatus(status) {
            if (status === 'edit' || status === 'create') {
                this.getNomineeShareholderRequireData();
            }
        },

        autoFillRequestedShareholderNominator()
        {
            if(this.autoFillRequestedShareholderNominator)
            {
                if(this.isNewNominator) {
                    this.resetNewNominatorFields("data-only");

                    if (this.autoFillRequestedShareholderNominator?.user_info?.id_type)
                    {
                        this.newNominator.idType = {
                            id: this.autoFillRequestedShareholderNominator?.user_info?.id_type?.id,
                            name: this.autoFillRequestedShareholderNominator?.user_info?.id_type?.name
                        };
                    }
                    if (this.autoFillRequestedShareholderNominator?.user_info?.nationality)
                    {
                        this.newNominator.nationality = {
                            text: this.autoFillRequestedShareholderNominator?.user_info?.nationality?.name,
                            value: this.autoFillRequestedShareholderNominator?.user_info?.nationality?.id
                        };
                    }
                    this.newNominator.dateOfExpiry = formatDate(this.autoFillRequestedShareholderNominator?.user_info?.date_of_expiry);

                    this.newNominator.nric = this.autoFillRequestedShareholderNominator?.user_info?.NRIC;
                    // this.newNominator.user_id = this.autoFillRequestedShareholderNominator.id;
                    this.newNominator.name = this.autoFillRequestedShareholderNominator?.user_info?.legal_name ?? this.autoFillRequestedShareholderNominator?.name;
                    this.newNominator.dob = formatDate(this.autoFillRequestedShareholderNominator?.user_info?.DOB);

                    if (this.autoFillRequestedShareholderNominator?.user_info?.country)
                    {
                        this.newNominator.country = {
                            text: this.autoFillRequestedShareholderNominator?.user_info?.country?.name,
                            value: this.autoFillRequestedShareholderNominator?.user_info?.country?.id
                        };
                    }

                    this.newNominator.contactNumber = this.autoFillRequestedShareholderNominator?.user_info?.contact_number;
                    this.newNominator.address = this.autoFillRequestedShareholderNominator?.user_info?.address;

                    if
                    (
                        ! this.autoFillRequestedShareholderNominator?.user_info?.nationality 
                        && this.autoFillRequestedShareholderNominator?.user_info?.id_type?.name === "NRIC (Citizen)"
                    )
                    {
                        this.newNominator.nationality = { value: 169, text: "SINGAPORE CITIZEN" };
                    }
                }
                
                if(this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL) {
                    if(this.isNewNominator)
                        this.newNominator.alias = this.autoFillRequestedShareholderNominator?.individual_shareholder?.alias;
                    this.nomineeShareholderLocal.alias = this.autoFillRequestedShareholderNominator?.individual_shareholder?.alias;
                }

                if(this.nomineeShareholderLocal.nominatorType.text === NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE) {
                    this.nomineeShareholderLocal.uen = this.autoFillRequestedShareholderNominator?.user_info?.uen;
                    this.nomineeShareholderLocal.statue = this.autoFillRequestedShareholderNominator?.shareholder?.statue;
                    this.nomineeShareholderLocal.registrar = this.autoFillRequestedShareholderNominator?.shareholder?.registrar;
                    this.nomineeShareholderLocal.jurisdiction = this.autoFillRequestedShareholderNominator?.shareholder?.jurisdiction;
                    this.nomineeShareholderLocal.legalForm = this.autoFillRequestedShareholderNominator?.shareholder?.legal_form ? {
                        value: this.autoFillRequestedShareholderNominator.shareholder.legal_form,
                        text: this.autoFillRequestedShareholderNominator.shareholder.legal_form
                    } : null;
                }

                this.forceRecheckExpiryDateCount += 1;
                this.forceToCheckExpiry = true;
            }
        },

        'nomineeShareholderLocal.nominatorType': {
            handler(type) {
                this.nominators = [];
                if(type) this.reInitializeNominators(type.text);
                this.reInitializeStatueAndRegistrarOptions();
            }
        },

        'newNominator.idType': {
            handler(data) {
                if(data && data.id) {
                    this.shouldDisabledNationality = data.id === NRIC_CITIZEN_ID;
                }
                else this.shouldDisabledNationality = false;
            }
        },

        isNomineeShareholderSidebarActive(isActive) {
            if((this.dataStatus !== 'create') && isActive)
                this.shouldShowNominatorDetails();
        },
    },

    async created() {
        this.reInitializeNominators(this.nomineeShareholderLocal.nominatorType ? this.nomineeShareholderLocal.nominatorType.text : NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL);
        this.getNomineeShareholderOptions();
    },

    methods: {
        async getNomineeShareholderRequireData() {
            this.clearExistingNomineeData();

            Nominator.getCorporateData()
                .then(payload => {
                    this.jurisdictions = payload.jurisdiction;
                    this.statues = payload.statue;
                    this.dataStatues = this.statues;
                    this.legalForms = payload.legal_form.map(lf => ({
                        text: lf,
                        value: lf,
                    }));
                    this.registrars = payload.registrar;
                    this.dataRegistrars = this.registrars;

                    // if nominee shareholder edit status then must be reinitialize be registrar and statue options
                    // according jurisdiction selected value
                    if(this.dataStatus === 'edit') this.reInitializeStatueAndRegistrarOptions();
                })
                .catch(e => 
                    console.log('Error while fetching corporate data', e)
                );

            // get nominee shareholder
            this.getNomineeShareholderOptions();

            // get nominator type options
            const nominatorTypeOptionsData = await CompanyNomineeShareholder.getNominatorTypeOptions();
            this.nominatorTypeOptions = nominatorTypeOptionsData.map(opt => {
                const text  = opt;
                const value  = opt;
                return { text, value };
            });

            // get id type options
            this.idType_options = await IdType.all();

            // get nationality options
            const nationalities = await Nationality.all();
            this.nationalityOptions = nationalities.map(data => ({ 
                value: data.id, 
                text: data.name 
            }));

            // get countries options
            const countries = await Country.all();
            this.countriesOptions = countries.data.map(data => ({ 
                value: data.id, 
                text: data.name 
            }));

            // nominator options
            CompanyNomineeShareholder.getNominatorOptions()
                .then(dataNominators => {
                    this.nominatorOptions = dataNominators.map(nominator => ({
                        id: nominator.id,
                        user_id: nominator.user.id,
                        nric: nominator.user.user_info.NRIC,
                        type: this.getNominatorType(nominator.shareholder_type),
                        name: nominator.user?.user_info?.legal_name ?? nominator.user.name,
                        email: nominator.user.email
                    }));

                    // if nominee shareholder edit status then must be reinitialize nominator options
                    // according nominee shareholder selection
                    this.reInitializeNominators(this.nomineeShareholderLocal.nominatorType?.text); 
                })
                .catch(e => console.log("Error while getting nominator options", e));
        },

        async getNomineeShareholderOptions() {
            const nomineeShareholders = await Shareholder.getShareholdersList();

            this.nomineeShareholderOptions = nomineeShareholders.map(nomineeShareholder => ({
                id: nomineeShareholder.id,
                user_id: nomineeShareholder.user.id,
                name: nomineeShareholder.user?.user_info?.legal_name ?? nomineeShareholder.user.name,
                email: nomineeShareholder.user.email,
            }));

            this.originNomineeShareholderData = this.nomineeShareholderOptions;
        },

        clearExistingNomineeData() {
            this.statues = [];
            this.legalForms = [];
            this.registrars = [];
            this.dataStatues = [];
            this.jurisdictions = [];
            this.dataRegistrars = [];
            this.nominatorTypeOptions = [];
            this.nominators = [];
            this.nomineeShareholderOptions = [];
        },

        reInitializeNominators(type)
        { 
            this.nominators = this.nominatorOptions.filter(
                nominatorOption => 
                    nominatorOption.type === type &&
                    nominatorOption.user_id !== this.nomineeShareholderLocal.nomineeShareholder?.user_id
            ).map(nominator => ({
                user_id: nominator.user_id,
                nric: nominator.nric,
                name: nominator.name,
                email: nominator.email,
                type: nominator.type
            }));
        },

        handleOnchangeNomineeShareholder(nomineeShareholder) {
            if(nomineeShareholder.user_id === this.nomineeShareholderLocal?.nominator?.user_id)
            {
                this.nomineeShareholderLocal.nominator = null;
                this.nomineeShareholderLocal.alias = null;
            }
            this.reInitializeNominators(this.nomineeShareholderLocal.nominatorType?.text);
        },

        async onNominatorTypeChange(payload)
        {
            this.nomineeShareholderLocal.alias = null;
            this.nomineeShareholderLocal.nominator = null;
            this.reInitializeNominators(payload.value);

            this.resetNomineeShareholderNominatorLocal();
            this.resetNewNominatorFields("data-only");
            
            // clear and recheck validation if nominator type change
            this.clearForm();
        },

        onNominatorJurisdictionChange(payload)
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === payload.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === payload.id);
            this.nomineeShareholderLocal.statue = '';
            this.nomineeShareholderLocal.registrar = '';
        },

        reInitializeStatueAndRegistrarOptions()
        {
            this.statues = this.dataStatues.filter(statue => statue.jurisdiction_id === this.nomineeShareholderLocal?.jurisdiction?.id);
            this.registrars = this.dataRegistrars.filter(registrar => registrar.jurisdiction_id === this.nomineeShareholderLocal?.jurisdiction?.id);
        },

        handleNationalityOnIdTypeChanged(data) {
            // intended when nric citizen or nric permanent user auto load change to passport or fin
            // this is just force to fire fire computed shouldShowExpiryDate property
            if(this.forceToCheckExpiry) {
                this.forceRecheckExpiryDateCount += 1;
            }
            
            this.shouldDisabledNationality = data.id === NRIC_CITIZEN_ID;

            if(data.id === NRIC_CITIZEN_ID) {
                const singaporeCitizen = this.nationalityOptions.filter(
                    nationality => nationality.text === SINGAPORE_CITIZEN
                ).map(
                    nation => ({
                        text: nation.text,
                        value: nation.value
                    })
                )[0];

                this.newNominator.nationality = singaporeCitizen;
            }
        },

        handleOnNominatorChange(nominator) {
            // this.nomineeShareholderLocal.nominatorType = {
            //     text: nominator.type,
            //     value: nominator.type
            // };

            this.requestAutoFillFormData(nominator.nric);
        },

        getNominatorType(type) {
            if(type.toLowerCase().includes(NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL))
                return NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.INDIVIDUAL;
            
            return NOMINEE_SHAREHOLDER.NOMINATOR.TYPE.CORPORATE;
        },

        shouldShowNominatorDetails() {
            // nominator details only show when the nominator is not a company shareholder(nominee shareholder list)
            // if nominator is company shareholder then we can view details in shareholder module
            const nomineeShareholderUserIds = this.originNomineeShareholderData.map(nomineeShareholder => nomineeShareholder.email.toLowerCase());
            
            const nominatorEmail = this.dataNomineeShareholder.nominator.email.toLowerCase();

            if(! nomineeShareholderUserIds.includes(nominatorEmail)) {
                this.showNominatorField("new");

                this.newNominator.user_id = this.dataNomineeShareholder.nominator.user_id;
                this.newNominator.nric = this.dataNomineeShareholder.nominator.nric;
                this.newNominator.name = this.dataNomineeShareholder.nominator.name;
                this.newNominator.alias = this.dataNomineeShareholder.nominator.alias;
                this.newNominator.idType = this.dataNomineeShareholder.nominator.idType;
                this.newNominator.nationality = this.dataNomineeShareholder.nominator.nationality;
                this.newNominator.dateOfExpiry = this.dataNomineeShareholder.nominator.dateOfExpiry;
                this.newNominator.country = this.dataNomineeShareholder.nominator.country;
                this.newNominator.email = this.dataNomineeShareholder.nominator.email;
                this.newNominator.dob = this.dataNomineeShareholder.nominator.dob;
                this.newNominator.contactNumber = this.dataNomineeShareholder.nominator.contactNumber;
                this.newNominator.address = this.dataNomineeShareholder.nominator.address;
            }
        },

        // eslint-disable-next-line func-names
        requestAutoFillFormData: debounce(function(id) {
            // if nominator type not select then it load data as individual
            if(this.nomineeShareholderLocal.nominatorType) {
                this.$emit('request-autofill-shareholder-nominator-data', {
                    id,
                    type: this.nomineeShareholderLocal.nominatorType.text
                });
            }
        }, 500),
    },

    setup(props, { emit }) {
        // eslint-disable-next-line no-shadow
        function useNomineeShareholderHandler(props, emit) {
            const isExistingNominator = ref(true);
            const isNewNominator = ref(false);
            const newNominator = ref({
                user_id: undefined,
                nric: undefined,
                name: undefined,
                alias: undefined,
                idType: undefined,
                nationality: undefined,
                dateOfExpiry: undefined,
                country: undefined,
                email: undefined,
                dob: undefined,
                contactNumber: undefined,
                address: undefined,
            });

            const nomineeShareholderLocal = ref(JSON.parse(JSON.stringify(props.dataNomineeShareholder.value)));

            const resetNomineeShareholderLocal = () => {
                // eslint-disable-next-line no-use-before-define
                clearForm();
                nomineeShareholderLocal.value = JSON.parse(JSON.stringify(props.dataNomineeShareholder.value));
            };

            const resetNomineeShareholderNominatorLocal = () => {
                nomineeShareholderLocal.value.nominator = null;
                nomineeShareholderLocal.value.alias = null;
                nomineeShareholderLocal.value.uen = null;
                nomineeShareholderLocal.value.jurisdiction = null;
                nomineeShareholderLocal.value.statue = null;
                nomineeShareholderLocal.value.registrar = null;
                nomineeShareholderLocal.value.legalForm = null;
            };

            const resetNewNominatorFields = (resetType = '') => {
                if(resetType === "data-only") {
                    Object.keys(newNominator.value).forEach(key => {
                        newNominator.value[key] = null;
                    });
                    return;
                }
                isExistingNominator.value = true;
                isNewNominator.value = false;
                newNominator.value = {};
            };

            watch(props.dataNomineeShareholder, () => {
                resetNomineeShareholderLocal();
            });
           
            const onSubmit = () => {
                const nomineeShareholderData = JSON.parse(JSON.stringify(nomineeShareholderLocal));

                if(isNewNominator.value) {
                    // if new nominator is set then it will override existing nominator value with new nominator
                    // if nominator include id then it's existing nominator otherwise it is new nominator
                    // this logic has used to know existing or new nominator in backend
                    nomineeShareholderData.value.nominator = newNominator.value;
                    nomineeShareholderData.value.isExistingNominator = false;

                }
                else {
                    nomineeShareholderData.value.nominator.alias = nomineeShareholderData.value.alias;
                    nomineeShareholderData.value.isExistingNominator = true;
                }

                if (props.dataNomineeShareholder.value.id) emit('update-nominee-shareholder', nomineeShareholderData.value);
                else emit('add-nominee-shareholder', nomineeShareholderData.value);
            };

            return {
                nomineeShareholderLocal, 

                // UI
                onSubmit,
                resetNomineeShareholderLocal,

                isExistingNominator,
                isNewNominator,
                newNominator,
                resetNewNominatorFields,
                resetNomineeShareholderNominatorLocal,
            };
        };
         
        const {
            nomineeShareholderLocal,

            // UI
            onSubmit, 
            resetNomineeShareholderLocal,
            resetNomineeShareholderNominatorLocal,

            isExistingNominator,
            isNewNominator,
            newNominator,
            resetNewNominatorFields,
        } = useNomineeShareholderHandler(toRefs(props), emit);

        const { refFormObserver, getValidationState, resetForm, clearForm } = formValidation(nomineeShareholderLocal);

        const showNominatorField = (value, status = 'view') => {
            if(value === 'new') {
                isExistingNominator.value = false;
                isNewNominator.value = true;
            }

            if(value === 'existing') {
                isExistingNominator.value = true;
                isNewNominator.value = false;
            }

            if(status !== 'view') {
                resetNomineeShareholderNominatorLocal();
                resetNewNominatorFields("data-only");
            }
        };

        function clearFormData() {
            resetNomineeShareholderLocal();
            resetNewNominatorFields();
            clearForm();
        };

        return {
            // Add New
            onSubmit,
            nomineeShareholderLocal,

            // Form Validation
            resetForm,
            clearForm,
            refFormObserver,
            getValidationState, 
            
            // Methods
            clearFormData,
            showNominatorField,
            resetNewNominatorFields,
            resetNomineeShareholderNominatorLocal,

            isExistingNominator,
            isNewNominator,

            newNominator,
        };
    },
};
</script>

<style scope>
.nominee-shareholder-create-or-edit .preloader {
    width: 15px;
    height: 15px;
}

.nominee-shareholder-create-or-edit .preloader div:before {
    background: var(--primary-color);
}

.nominee-shareholder-create-or-edit .action-button .preloader div:before {
    background: white;
}

.side-bar-header{
   background: #f6f6f6 !important;
 } 
 
/* select disabled color overwrite */
.vs--disabled .vs__dropdown-toggle {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__search {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__open-indicator {
    background: #efefef!important;
    cursor: context-menu!important;
}
.vs--disabled .vs__selected{
    cursor: context-menu!important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
@import "@core/scss/vue/libs/quill.scss";
</style>